import React, { useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { Modal, Alert, Typography, Checkbox as AntCheckbox } from 'antd'
import { SyncOutlined, WarningOutlined } from '@ant-design/icons'
import { FormattedMessage, useIntl } from 'react-intl'
import dayjs from 'dayjs'

import { CourseContext } from '../../Course/CourseContext'
import { Course } from '../../Course/types'
import { FlexRow } from '../../FlexboxReusables/FlexItems'

type FinalModalBeforePublishProps = {
  course: Course
  startDate: dayjs.Dayjs
  examDuration: number
  maxTries: number
  showModal: boolean
  setShowModal: (showModal: boolean) => void
}

const FinalModalBeforePublish: React.FC<FinalModalBeforePublishProps> = (props: FinalModalBeforePublishProps) => {
  const { course, startDate, examDuration, maxTries, showModal, setShowModal } = props

  const [ hasAcknowledgedCheckbox, setHasAcknowledgedCheckbox ] = useState<boolean>(false)
  const [ isCurrentlyPublishing, setIsCurrentlyPublishing ] = useState<boolean>(false)

  const { publishCourse } = useContext(CourseContext)
  const navigate = useNavigate()

  const intl = useIntl()

  const handlePublish = async () => {
    if (!hasAcknowledgedCheckbox) {
      return
    }
    
    setIsCurrentlyPublishing(true)
    const updatedCourse = updateCourseWithPublishingData(
      course,
      startDate,
      examDuration,
      maxTries
    )

    try {
      await publishCourse(updatedCourse, intl)
      setShowModal(false)

      navigate(`/`)
    } finally { // no catch, errors are handled in publishCourse context method
      setIsCurrentlyPublishing(false)
    }
  }

  const updateCourseWithPublishingData = (
    course: Course,
    startDate: dayjs.Dayjs,
    examDuration: number,
    maxTries: number
  ) => {
    const updatedCourse = {
      ...course,
      examStartsAt: convertDayJsDateToDbFormat(startDate),
      examDurationInMinutes: examDuration,
      maxTries
    }
    console.log('updatedCourse', updatedCourse)
    return updatedCourse
  }

  const convertDayJsDateToDbFormat = (date: dayjs.Dayjs) => date.toISOString()

  return (
    <>
      <Modal
        centered
        title={<FormattedMessage id='course.publish' />}
        open={showModal}
        confirmLoading={isCurrentlyPublishing}
        onOk={handlePublish}
        okText={<FormattedMessage id='generic.publish' />}
        okButtonProps={{ disabled: !hasAcknowledgedCheckbox || course.isPublished }}
        onCancel={() => setShowModal(false)}
        cancelText={<FormattedMessage id='generic.cancel' />}
        width={'50%'}
      >
        {!isCurrentlyPublishing ? (
          <>
            <Alert
              type="info"
              message={
                <>
                  <FlexRow justifyContent='center' margin='0 0 1em 0' alignItems='center'>
                    <WarningIcon />
                    <Typography.Title level={3}>
                      <FormattedMessage id='generic.hint' />
                    </Typography.Title>
                  </FlexRow>
                  <FlexRow margin='1em 0'>
                    <Typography.Text>
                      <FormattedMessage id='course.publish.beforePublishingInfo1' />
                    </Typography.Text>
                  </FlexRow>
                  <FlexRow margin='1em 0'>
                    <ul>
                      <ListItem>
                        <Typography.Text strong>
                          <FormattedMessage id='course.publish.beforePublishingInfo2' />
                        </Typography.Text>
                      </ListItem>
                      <ListItem>
                        <Typography.Text strong>
                          <FormattedMessage id='course.publish.beforePublishingInfo3' />
                        </Typography.Text>
                      </ListItem>
                    </ul>
                  </FlexRow>
                </>
              }
            />
            <FlexRow justifyContent='flex-start' margin='1em 0'>
              <StyledCheckbox
                onChange={(e) => setHasAcknowledgedCheckbox(e.target.checked)}
                checked={hasAcknowledgedCheckbox}
              >
                <Typography.Text>
                  <FormattedMessage id='course.publish.acknowledgedInfo' />
                </Typography.Text>
              </StyledCheckbox>
            </FlexRow>
            <FlexRow justifyContent='flex-start' margin='2em 0'>
              <Typography.Text strong>
                <FormattedMessage id='course.publish.confirmPublish' />
              </Typography.Text>
            </FlexRow>
          </>
        ) : (
          <FlexRow alignItems='center' justifyContent='flex-start'>
            <SyncIcon spin />
            <Typography.Text>
              <FormattedMessage id='course.publish.isCurrentlyPublishing' />
            </Typography.Text>
          </FlexRow>
        )}
      </Modal>
    </>
  )
}

export default FinalModalBeforePublish

const ListItem = styled.li`
  margin-bottom: 10px;
`

const StyledCheckbox = styled(AntCheckbox)`
  .ant-checkbox-inner {
    border-color: ${props => !props.checked ? 'red' : ''} !important;
  }
`

const SyncIcon = styled(SyncOutlined)`
  font-size: 1.2em;
  color: #1890ff;
  margin-right: 0.5em;
`

const WarningIcon = styled(WarningOutlined)`
    font-size: 1.6em;
    color: red;
    margin-right: 0.5em;
    margin-bottom: 0.4em;
`
